import React, { useEffect } from 'react';
import { toast } from 'react-hot-toast';
import { useAuth } from '../contexts/AuthContext';
import { authAPI } from '../services/api';
import { Bus as BusIcon, LogIn } from 'lucide-react';

declare global {
  interface Window {
    google: any;
  }
}

interface LoginProps {
  onUnauthorizedUser?: (userInfo: any) => void;
}

export const Login: React.FC<LoginProps> = ({ onUnauthorizedUser }) => {
  const { login } = useAuth();

  useEffect(() => {
    // Load Google OAuth script
    const script = document.createElement('script');
    script.src = 'https://accounts.google.com/gsi/client';
    script.async = true;
    script.defer = true;
    document.body.appendChild(script);

    script.onload = () => {
      if (window.google) {
        window.google.accounts.id.initialize({
          client_id: process.env.REACT_APP_GOOGLE_CLIENT_ID,
          callback: handleCredentialResponse,
        });

        window.google.accounts.id.renderButton(
          document.getElementById('google-signin-button'),
          {
            theme: 'outline',
            size: 'large',
            width: 300,
            text: 'signin_with',
            shape: 'rectangular',
          }
        );
      }
    };

    return () => {
      document.body.removeChild(script);
    };
  }, []);

  const handleCredentialResponse = async (response: any) => {
    try {
      const result = await authAPI.googleAuth(response.credential);
      
      if (result.status === 'success') {
        login(result.data.token, result.data.user);
        toast.success(`Welcome, ${result.data.user.name}!`);
        // Clear any unauthorized user info
        localStorage.removeItem('unauthorized_user_info');
      } else {
        toast.error(result.message);
      }
    } catch (error: any) {
      console.error('Login error:', error);
      
      if (error.response?.status === 403 && error.response?.data?.code === 'UNAUTHORIZED_EMAIL') {
        // Handle unauthorized user
        const userInfo = error.response.data.user_info;
        if (userInfo && onUnauthorizedUser) {
          // Store user info for access request page
          localStorage.setItem('unauthorized_user_info', JSON.stringify(userInfo));
          onUnauthorizedUser(userInfo);
        } else {
          toast.error(error.response.data.message);
        }
      } else if (error.response?.data?.message) {
        toast.error(error.response.data.message);
      } else {
        toast.error('Login failed. Please try again.');
      }
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 flex items-center justify-center px-4">
      <div className="max-w-md w-full space-y-8">
        <div className="text-center">
          <div className="flex justify-center mb-6">
            <div className="bg-blue-600 p-4 rounded-2xl shadow-lg">
              <BusIcon className="h-16 w-16 text-white" />
            </div>
          </div>
          <h2 className="text-3xl font-bold text-gray-900 mb-2">
            TNSTC Bus Monitor
          </h2>
          <p className="text-gray-600 mb-8">
            Real-time bus availability tracking system
          </p>
        </div>

        <div className="bg-white rounded-2xl shadow-xl p-8">
          <div className="text-center mb-6">
            <LogIn className="h-12 w-12 text-blue-600 mx-auto mb-4" />
            <h3 className="text-xl font-semibold text-gray-900 mb-2">
              Sign in to continue
            </h3>
            <p className="text-gray-600 text-sm">
              Use your Google account to access the bus monitoring system
            </p>
          </div>

          <div className="flex justify-center">
            <div id="google-signin-button"></div>
          </div>

          <div className="mt-6 p-4 bg-blue-50 border border-blue-200 rounded-lg">
            <div className="flex">
              <div className="flex-shrink-0">
                <BusIcon className="h-5 w-5 text-blue-600" />
              </div>
              <div className="ml-3">
                <h4 className="text-sm font-medium text-blue-800">Access Required</h4>
                <p className="text-sm text-blue-700 mt-1">
                  Only authorized users can access this application. If you don't have access, 
                  you can request it after signing in with Google.
                </p>
              </div>
            </div>
          </div>
        </div>

        <div className="text-center text-sm text-gray-500">
          <p>Secure authentication powered by Google OAuth 2.0</p>
        </div>
      </div>
    </div>
  );
}; 